import { getAuthHeader, getPlatform, getWebAppUser } from './platform';
import { trackGoal } from './utils/metrika';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  async request(endpoint, options = {}) {
    const authHeader = getAuthHeader();
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        ...authHeader,
        ...options.headers,
      },
      cache: 'no-store',
      ...options,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      // Если 401 (expired или потерян initData) — перезагружаем приложение (только один раз)
      if (response.status === 401 && error.detail) {
        const isAuthError = error.detail.includes('expired') ||
                            error.detail.includes('Требуется авторизация') ||
                            error.detail.includes('Missing');
        if (isAuthError) {
          const reloadKey = 'stems_reload_expired';
          const lastReload = localStorage.getItem(reloadKey);
          const now = Date.now();
          if (!lastReload || now - parseInt(lastReload) > 30000) {
            console.log('Auth lost, reloading...', error.detail);
            localStorage.setItem(reloadKey, now.toString());
            trackGoal('auth_reload');
            window.location.reload();
            return;
          }
        }
      }
      const err = new Error(error.detail || 'Request failed');
      // Код и тело ответа — тем, кому важно «нет такого» (404) или «в архиве» (410)
      err.status = response.status;
      err.body = error;
      throw err;
    }

    return response.json();
  }

  // Позиция по ссылке, которой нет в списке витрины: название, фото, раздел для окна
  // «Сейчас нет в продаже» (backend seo_pages.item_card; архив — 410 с тем же телом)
  async getItemCard(kind, id) {
    return this.request(`/seo/page/item/${kind}/${id}`);
  }

  // Подборки по цветку (модуль рекламы по наличию): { slug, title, items: [как /products] }
  async getCollection(slug) {
    return this.request(`/seo/collections/${encodeURIComponent(slug)}`);
  }

  // [{ slug, title, count }] — только подборки, где есть что купить сегодня
  async getCollections() {
    return this.request('/seo/collections');
  }

  // Blocked Slots
  async getBlockedSlotsForDate(date) {
    return this.request(`/settings/blocked-slots/check?date=${date}`);
  }

  async getFullyBlockedDates() {
    return this.request('/settings/blocked-slots/fully-blocked');
  }

  // Products
  async getProducts(category = null, filter = null, page = 1, limit = 20) {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (filter) params.append('filter', filter);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    const query = `?${params.toString()}`;
    return this.request(`/products${query}`);
  }

  async getProductsByIds(ids) {
    return this.request('/products/by-ids', {
      method: 'POST',
      body: JSON.stringify(ids),
    });
  }

  // Categories
  async getCategories(scope = null) {
    const q = scope ? `?scope=${scope}` : '';
    return this.request(`/categories${q}`);
  }

  // Bouquets
  async getBouquets({ kind = null, date = null, page = 1, limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (kind) params.append('kind', kind);
    if (date) params.append('date', date);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return this.request(`/bouquets?${params.toString()}`);
  }

  async getBouquet(id, date = null) {
    const q = date ? `?date=${date}` : '';
    return this.request(`/bouquets/${id}${q}`);
  }

  async getBouquetAvailableDates(bouquetId, quantity = 1, days = null) {
    // days=null → backend возьмёт catalog_horizon_days из Settings.
    const body = { bouquet_id: bouquetId, quantity };
    if (days != null) body.days = days;
    return this.request('/bouquets/available-dates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Settings
  async getSettings() {
    return this.request('/settings');
  }

  // Pickup Points
  async getPickupPoints() {
    return this.request('/settings/pickup-points');
  }

  // Users
  async getOrCreateUser(telegramData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(telegramData),
    });
  }

  async getUser(telegramId) {
    return this.request(`/users/${telegramId}`);
  }

  // Orders
  async createOrder(orderData) {
    return this.request('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  // Получить payment_url для оплаты (использует сохранённую ссылку
  // или создаёт новую если её нет). Заменяет legacy /payment/go/order/{id}.
  async getOrderPaymentUrl(orderId) {
    return this.request(`/payment/order-url/${orderId}`, { method: 'POST' });
  }

  // Ссылка нативного Telegram-инвойса (openInvoice). 503 — провайдер не
  // подключён у инстанса, фронт откатывается на redirect-флоу.
  // type: undefined — заказ, 'cert' — сертификат, 'sub' — подписка (карта).
  async createTgInvoice(orderId, type) {
    return this.request(`/payment/tg-invoice/${orderId}${type ? `?type=${type}` : ''}`, { method: 'POST' });
  }

  async getSubscriptionPaymentUrl(subscriptionId) {
    return this.request(`/payment/subscription-url/${subscriptionId}`, { method: 'POST' });
  }

  // Подарочные сертификаты теперь standalone (отдельная таблица).
  async getCertificatePaymentUrl(certId) {
    return this.request(`/payment/cert-url/${certId}`, { method: 'POST' });
  }

  async getMyCertificatePurchases() {
    return this.request('/certificates/my-purchases');
  }

  async resendCertificate(certId) {
    return this.request(`/certificates/${certId}/resend`, { method: 'POST' });
  }

  async createPayment(orderId) {
    return this.request(`/payment/create/${orderId}`, {
      method: 'POST',
    });
  }

  async getPaymentStatus(orderId) {
    return this.request(`/payment/status/${orderId}`);
  }

  async getUserOrders(telegramId) {
    return this.request(`/orders/user/${telegramId}`);
  }

  // Заказы web-юзера по JWT
  async getCustomerOrders() {
    return this.request('/orders/customer/me');
  }

  // Subscriptions
  async getSubscriptionCatalog() {
    return this.request('/subscriptions/catalog');
  }

  async getSubscriptionDiscountTiers() {
    return this.request('/subscriptions/discount-tiers');
  }

  async createSubscription(data) {
    return this.request('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createSubscriptionPayment(subscriptionId) {
    return this.request(`/subscriptions/${subscriptionId}/pay`, {
      method: 'POST',
    });
  }

  async uploadPartnerCard(subscriptionId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}/partner-card`, {
      method: 'POST',
      body: formData,
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Ошибка загрузки файла');
    }
    return response.json();
  }

  async getMySubscriptions(telegramId) {
    return this.request(`/subscriptions/my?telegram_id=${telegramId}`);
  }

  // Подписки web-юзера по JWT
  async getCustomerSubscriptions() {
    return this.request('/subscriptions/customer/me');
  }

  async updateSubscriptionDeliveryTime(subscriptionId, orderId, deliveryTime, telegramId) {
    const qs = telegramId ? `?telegram_id=${telegramId}` : '';
    return this.request(`/subscriptions/${subscriptionId}/delivery-time${qs}`, {
      method: 'PATCH',
      body: JSON.stringify({ order_id: orderId, delivery_time: deliveryTime }),
    });
  }

  // Account Link (объединение аккаунтов)
  async createLinkToken(platformId) {
    return this.request('/users/link/create', {
      method: 'POST',
      body: JSON.stringify({ platform_id: platformId }),
    });
  }

  // Для web-инициатора: ссылку генерируем по JWT + target_platform ('telegram' | 'max')
  async createLinkTokenWeb(targetPlatform) {
    return this.request('/users/link/create', {
      method: 'POST',
      body: JSON.stringify({ target_platform: targetPlatform }),
    });
  }

  async confirmLinkToken(token, platformId) {
    return this.request('/users/link/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, platform_id: platformId }),
    });
  }

  // Delivery Calculation
  // Gift certificates
  async getCertificateSettings() {
    return this.request('/certificates/settings');
  }

  async getCertificateBuyerProfile() {
    return this.request('/certificates/me/profile');
  }

  async purchaseCertificate(payload) {
    return this.request('/certificates/purchase', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async activateCertificate(code) {
    return this.request('/certificates/activate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async getMyCertificates() {
    return this.request('/certificates/my');
  }

  async calculateDelivery(address, lat = null, lon = null) {
    let url = `/delivery/calculate?address=${encodeURIComponent(address)}`;
    if (lat !== null && lon !== null) {
      url += `&lat=${lat}&lon=${lon}`;
    }
    return this.request(url);
  }
}

export const api = new ApiService();

export function pluralize(n, one, few, many) {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

// Сохраняем/читаем данные пользователя из localStorage (переживает redirect из ЮКассы)
const PLATFORM_USER_KEY = 'stems_platform_user';
const TG_USER_KEY = 'stems_tg_user'; // legacy — для миграции

function savePlatformUser(userData) {
  try {
    localStorage.setItem(PLATFORM_USER_KEY, JSON.stringify(userData));
  } catch (e) { /* ignore */ }
  return userData;
}

function loadSavedPlatformUser() {
  try {
    // Пробуем новый ключ
    const saved = localStorage.getItem(PLATFORM_USER_KEY);
    if (saved) return JSON.parse(saved);
    // Миграция со старого ключа
    const legacy = localStorage.getItem(TG_USER_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      // Конвертируем в новый формат
      if (parsed.telegram_id && !parsed.platform) {
        parsed.platform = 'telegram';
      }
      savePlatformUser(parsed);
      return parsed;
    }
  } catch (e) { /* ignore */ }
  return null;
}

// Получаем данные пользователя из WebApp (Telegram или Max)
export function getPlatformUser() {
  const platform = getPlatform();

  // 1. Пробуем получить из SDK (Telegram или Max)
  const webAppUser = getWebAppUser();
  if (webAppUser) {
    return savePlatformUser({
      telegram_id: platform === 'telegram' ? webAppUser.id.toString() : undefined,
      max_id: platform === 'max' ? webAppUser.id.toString() : undefined,
      first_name: webAppUser.first_name || '',
      last_name: webAppUser.last_name || '',
      username: webAppUser.username || '',
      photo_url: webAppUser.photo_url || undefined,
      language_code: webAppUser.language_code || undefined,
      platform,
    });
  }

  // 2. Fallback: URL-параметры (бот передаёт tg_id/max_id)
  const urlParams = new URLSearchParams(window.location.search);
  const tgId = urlParams.get('tg_id');
  const maxId = urlParams.get('max_id');
  if (tgId) {
    return savePlatformUser({
      telegram_id: tgId,
      first_name: urlParams.get('tg_fn') || '',
      last_name: urlParams.get('tg_ln') || '',
      username: urlParams.get('tg_un') || '',
      platform: 'telegram',
    });
  }
  if (maxId) {
    return savePlatformUser({
      max_id: maxId,
      first_name: urlParams.get('max_fn') || '',
      last_name: urlParams.get('max_ln') || '',
      username: urlParams.get('max_un') || '',
      platform: 'max',
    });
  }

  // 3. Восстанавливаем из localStorage (после redirect из ЮКассы)
  // ТОЛЬКО для TG/Max и только если платформа совпадает.
  // Для web identity приходит исключительно из JWT (см. ниже),
  // чтобы случайно не подтянуть чужие заказы из cached TG/Max сессии.
  if (platform !== 'web') {
    const saved = loadSavedPlatformUser();
    if (saved && saved.platform === platform) return saved;
  }

  // 3.5. Web: identity только из JWT
  if (platform === 'web') {
    const jwt = localStorage.getItem('stems_jwt');
    if (jwt) {
      // JWT-юзер без cached данных — вернём минимальный объект,
      // полные данные подтянутся через fetchWebUser()
      return { platform: 'web', first_name: '' };
    }
    return { platform: 'guest', first_name: '' };
  }

  // 4. Dev fallback — только для localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return {
      telegram_id: '71590340',
      first_name: 'Nikolay',
      last_name: 'Melnikov',
      username: 'melnikov_ny',
      platform: 'telegram',
    };
  }

  // Нет данных — пользователь не авторизован
  return null;
}

// Загрузка данных веб-юзера по JWT
export async function fetchWebUser() {
  const jwt = localStorage.getItem('stems_jwt');
  if (!jwt) return null;
  try {
    const res = await fetch(`${API_URL}/web-auth/me`, {
      headers: { 'Authorization': `Bearer ${jwt}` },
    });
    if (!res.ok) {
      if (res.status === 401) {
        localStorage.removeItem('stems_jwt');
      }
      return null;
    }
    const data = await res.json();
    return savePlatformUser({ ...data, platform: 'web' });
  } catch (e) {
    return null;
  }
}

// Обратная совместимость
export const getTelegramUser = getPlatformUser;

export function getImageUrl(imagePath) {
  if (!imagePath || typeof imagePath !== 'string') return null;
  if (imagePath.startsWith('http')) return imagePath;

  // Определяем базовый URL в зависимости от окружения
  let baseUrl;

  // Если запущено в мини-приложении или не на localhost — используем текущий origin
  if (getPlatform() !== 'web' || (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')) {
    baseUrl = window.location.origin;
  } else {
    // Для локальной разработки используем REACT_APP_API_URL
    baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  }

  return `${baseUrl}${imagePath}`;
}

